import { GPEventResponse } from "../../types";
import { PlayerStatType } from "../PlayerStats/definition";

export { GameProviderTagEnum } from "../definition";

export const MAX_CATEGORY_TABS = 20;
export const MAX_INPUT_CHARACTERS = 40;
export const MAX_CATEGORY_TABS_WITH_DROPDOWN = 6;

export const FIELD_MAP_SIZE = {
	width: 410,
	height: 614,
	padding: 20,
};

export const POINT_MAP_CONFIG = {
	size: 15,
	minLineLength: 15,
};

export enum ActionType {
	OnTargetBlocked = "blocked",
	Block = "block",
	PassReceived = "pass_received",
	Off = "off",
	On = "on",
	Save = "save",
	GoalConceded = "goal_conceded",
}

export type EventType =
	| EventOffensiveEnum
	| EventDefensiveEnum
	| EventDistributionEnum
	| EventGoalkeeperEnum;

export type WidgetItem = {
	category: InsightCategoryEnum;
	eventType: EventType;
	mapType?: MapType;
	isCustom?: boolean;
	parameters?: Array<Record<string, string>>;
	key: string;
	group: InsightEventGroupEnum;
	label: string;
	isPlaceholder?: boolean;
};

export enum MapType {
	PointMap = "point-map",
	Directional = "directional-map",
	Zonal = "zonal-map",
	Distribution = "distribution-chart",
	CulmulativeChart = "cumulative-progression-charts",
}

export const VISUAL_MAP_OPTIONS = [
	{
		label: "gamePage.dataInsights.mapType.PointMap",
		value: MapType.PointMap,
	},
	{
		label: "gamePage.dataInsights.mapType.directional",
		value: MapType.Directional,
		condition: (eventType: PlayerStatType) => {
			return [PlayerStatType.Pass].includes(eventType);
		},
	},
	{
		label: "gamePage.dataInsights.mapType.zonal",
		value: MapType.Zonal,
	},
	{
		label: "gamePage.dataInsights.mapType.distributionChart",
		value: MapType.Distribution,
	},
	{
		label: "gamePage.dataInsights.mapType.cumulativeProgressionCharts",
		value: MapType.CulmulativeChart,
	},
];

export const PERIODS_OPTIONS = [
	{
		label: "1st Half",
		value: "1",
	},
	{
		label: "2nd Half",
		value: "2",
	},
];

export enum InsightCategoryEnum {
	Offensive = "offensive",
	Defensive = "defensive",
	Distribution = "distribution",
	Goalkeeper = "goalkeeper",
}

/**
 * Event grouping
 * hard limit 20 groups, including default categories
 */
export enum InsightEventGroupEnum {
	Offensive = "offensive",
	Defensive = "defensive",
	Distribution = "distribution",
	Goalkeeper = "goalkeeper",
	CustomGroup1 = "customGroup1",
	CustomGroup2 = "customGroup2",
	CustomGroup3 = "customGroup3",
	CustomGroup4 = "customGroup4",
	CustomGroup5 = "customGroup5",
	CustomGroup6 = "customGroup6",
	CustomGroup7 = "customGroup7",
	CustomGroup8 = "customGroup8",
	CustomGroup9 = "customGroup9",
	CustomGroup10 = "customGroup10",
	CustomGroup11 = "customGroup11",
	CustomGroup12 = "customGroup12",
	CustomGroup13 = "customGroup13",
	CustomGroup14 = "customGroup14",
	CustomGroup15 = "customGroup15",
	CustomGroup16 = "customGroup16",
}

export enum EventOffensiveEnum {
	TechnicalError = "technicalError",
	TakeOn = "takeOn",
	Offside = "offside",
	Goal = "goal",
	FoulWon = "foulWon",
	Shot = "shot",
}

export enum EventDefensiveEnum {
	OwnGoals = "goalOwn",
	FoulCommited = "foulCommited",
	TakeOnAgainst = "takeOnAgainst",
	Block = "block",
	Clearance = "clearance",
	Card = "card",
	Interception = "interception",
	Tackle = "tackle",
	LoseBallRecovery = "loseBallRecovery",
}

export enum EventGoalkeeperEnum {
	CatchInterception = "catchInterception",
	Punch = "punch",
	GoalConceded = "goalConceded",
	Save = "save",
}

export enum EventDistributionEnum {
	AerialDuels = "aerialDuel",
	Pass = "pass",
	PassReceived = "passReceived",
}

export type EventParameter = {
	eventType: string;
	key: string;
	value: string;
};

export const InsightCategoryMap: {
	[InsightCategoryEnum.Offensive]: Record<
		EventOffensiveEnum,
		GPEventResponse[]
	>;
	[InsightCategoryEnum.Defensive]: Record<
		EventDefensiveEnum,
		GPEventResponse[]
	>;
	[InsightCategoryEnum.Distribution]: Record<
		EventDistributionEnum,
		GPEventResponse[]
	>;
	[InsightCategoryEnum.Goalkeeper]: Record<
		EventGoalkeeperEnum,
		GPEventResponse[]
	>;
} = {
	[InsightCategoryEnum.Offensive]: {
		[EventOffensiveEnum.TakeOn]: [],
		[EventOffensiveEnum.Offside]: [],
		[EventOffensiveEnum.Goal]: [],
		[EventOffensiveEnum.FoulWon]: [],
		[EventOffensiveEnum.Shot]: [],
		[EventOffensiveEnum.TechnicalError]: [],
	},
	[InsightCategoryEnum.Defensive]: {
		[EventDefensiveEnum.OwnGoals]: [],
		[EventDefensiveEnum.FoulCommited]: [],
		[EventDefensiveEnum.Block]: [],
		[EventDefensiveEnum.Interception]: [],
		[EventDefensiveEnum.LoseBallRecovery]: [],
		[EventDefensiveEnum.Card]: [],
		[EventDefensiveEnum.TakeOnAgainst]: [],
		[EventDefensiveEnum.Clearance]: [],
		[EventDefensiveEnum.Tackle]: [],
	},
	[InsightCategoryEnum.Distribution]: {
		[EventDistributionEnum.AerialDuels]: [],
		[EventDistributionEnum.Pass]: [],
		[EventDistributionEnum.PassReceived]: [],
	},
	[InsightCategoryEnum.Goalkeeper]: {
		[EventGoalkeeperEnum.CatchInterception]: [],
		[EventGoalkeeperEnum.Punch]: [],
		[EventGoalkeeperEnum.GoalConceded]: [],
		[EventGoalkeeperEnum.Save]: [],
	},
};

export const DEFAULT_WIDGET_SETTINGS: Record<
	InsightCategoryEnum,
	WidgetItem[]
> = {
	[InsightCategoryEnum.Offensive]: [
		{
			category: InsightCategoryEnum.Offensive,
			eventType: EventOffensiveEnum.Shot,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "shot-1",
			group: InsightEventGroupEnum.Offensive,
			label: InsightCategoryEnum.Offensive,
		},
		{
			category: InsightCategoryEnum.Offensive,
			eventType: EventOffensiveEnum.TakeOn,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "take-on-1",
			group: InsightEventGroupEnum.Offensive,
			label: InsightCategoryEnum.Offensive,
		},
		{
			category: InsightCategoryEnum.Offensive,
			eventType: EventOffensiveEnum.Goal,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "goal-1",
			group: InsightEventGroupEnum.Offensive,
			label: InsightCategoryEnum.Offensive,
		},
		{
			category: InsightCategoryEnum.Offensive,
			eventType: EventOffensiveEnum.FoulWon,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "foul-won-1",
			group: InsightEventGroupEnum.Offensive,
			label: InsightCategoryEnum.Offensive,
		},
		{
			category: InsightCategoryEnum.Offensive,
			eventType: EventOffensiveEnum.Offside,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "offside-1",
			group: InsightEventGroupEnum.Offensive,
			label: InsightCategoryEnum.Offensive,
		},
		{
			category: InsightCategoryEnum.Offensive,
			eventType: EventOffensiveEnum.TechnicalError,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "technical-error-1",
			group: InsightEventGroupEnum.Offensive,
			label: InsightCategoryEnum.Offensive,
		},
	],
	[InsightCategoryEnum.Defensive]: [
		{
			category: InsightCategoryEnum.Defensive,
			eventType: EventDefensiveEnum.OwnGoals,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "own-goals-1",
			group: InsightEventGroupEnum.Defensive,
			label: InsightCategoryEnum.Defensive,
		},
		{
			category: InsightCategoryEnum.Defensive,
			eventType: EventDefensiveEnum.FoulCommited,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "foul-commited-1",
			group: InsightEventGroupEnum.Defensive,
			label: InsightCategoryEnum.Defensive,
		},
		{
			category: InsightCategoryEnum.Defensive,
			eventType: EventDefensiveEnum.Block,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "block-1",
			group: InsightEventGroupEnum.Defensive,
			label: InsightCategoryEnum.Defensive,
		},
		{
			category: InsightCategoryEnum.Defensive,
			eventType: EventDefensiveEnum.Interception,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "interception-1",
			group: InsightEventGroupEnum.Defensive,
			label: InsightCategoryEnum.Defensive,
		},
		{
			category: InsightCategoryEnum.Defensive,
			eventType: EventDefensiveEnum.LoseBallRecovery,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "lose-ball-recovery-1",
			group: InsightEventGroupEnum.Defensive,
			label: InsightCategoryEnum.Defensive,
		},
		{
			category: InsightCategoryEnum.Defensive,
			eventType: EventDefensiveEnum.Tackle,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "tackle-1",
			group: InsightEventGroupEnum.Defensive,
			label: InsightCategoryEnum.Defensive,
		},
		{
			category: InsightCategoryEnum.Defensive,
			eventType: EventDefensiveEnum.Clearance,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "clearance-1",
			group: InsightEventGroupEnum.Defensive,
			label: InsightCategoryEnum.Defensive,
		},
		{
			category: InsightCategoryEnum.Defensive,
			eventType: EventDefensiveEnum.Card,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "card-1",
			group: InsightEventGroupEnum.Defensive,
			label: InsightCategoryEnum.Defensive,
		},
		{
			category: InsightCategoryEnum.Defensive,
			eventType: EventDefensiveEnum.TakeOnAgainst,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "take-on-against-1",
			group: InsightEventGroupEnum.Defensive,
			label: InsightCategoryEnum.Defensive,
		},
	],
	[InsightCategoryEnum.Distribution]: [
		{
			category: InsightCategoryEnum.Distribution,
			eventType: EventDistributionEnum.AerialDuels,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "aerial-duels-1",
			group: InsightEventGroupEnum.Distribution,
			label: InsightCategoryEnum.Distribution,
		},
		{
			category: InsightCategoryEnum.Distribution,
			eventType: EventDistributionEnum.Pass,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "pass-1",
			group: InsightEventGroupEnum.Distribution,
			label: InsightCategoryEnum.Distribution,
		},
		{
			category: InsightCategoryEnum.Distribution,
			eventType: EventDistributionEnum.PassReceived,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "pass-received-1",
			group: InsightEventGroupEnum.Distribution,
			label: InsightCategoryEnum.Distribution,
		},
	],
	[InsightCategoryEnum.Goalkeeper]: [
		{
			category: InsightCategoryEnum.Goalkeeper,
			eventType: EventGoalkeeperEnum.CatchInterception,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "catch-interception-1",
			group: InsightEventGroupEnum.Goalkeeper,
			label: InsightCategoryEnum.Goalkeeper,
		},
		{
			category: InsightCategoryEnum.Goalkeeper,
			eventType: EventGoalkeeperEnum.Punch,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "punch-1",
			group: InsightEventGroupEnum.Goalkeeper,
			label: InsightCategoryEnum.Goalkeeper,
		},
		{
			category: InsightCategoryEnum.Goalkeeper,
			eventType: EventGoalkeeperEnum.GoalConceded,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "goal-conceded-1",
			group: InsightEventGroupEnum.Goalkeeper,
			label: InsightCategoryEnum.Goalkeeper,
		},
		{
			category: InsightCategoryEnum.Goalkeeper,
			eventType: EventGoalkeeperEnum.Save,
			mapType: MapType.PointMap,
			isCustom: false,
			parameters: [],
			key: "save-1",
			group: InsightEventGroupEnum.Goalkeeper,
			label: InsightCategoryEnum.Goalkeeper,
		},
	],
};

/** Event widgets unavailable when only one team is tagged (teamTagged is home/away). */
export const ONE_TEAM_TAGGING_OMITTED_INSIGHT_EVENT_TYPES: ReadonlySet<string> = new Set([
	EventOffensiveEnum.TakeOn,
	EventOffensiveEnum.FoulWon,
	EventDefensiveEnum.LoseBallRecovery,
	EventDefensiveEnum.TakeOnAgainst,
]);

export const isOneTeamTagging = (teamTagged?: string | null): boolean =>
	teamTagged !== undefined && teamTagged !== null && teamTagged !== "both";

export const shouldOmitInsightEventForOneTeamTagging = (
	eventType: string,
	teamTagged?: string | null,
): boolean =>
	isOneTeamTagging(teamTagged)
	&& ONE_TEAM_TAGGING_OMITTED_INSIGHT_EVENT_TYPES.has(eventType);

export const filterWidgetsForTeamTagged = (
	widgets: WidgetItem[],
	teamTagged?: string | null,
): WidgetItem[] => {
	if (isOneTeamTagging(teamTagged) === false) {
		return widgets;
	}

	return widgets.filter(
		(widget) =>
			widget.isPlaceholder === true
			|| ONE_TEAM_TAGGING_OMITTED_INSIGHT_EVENT_TYPES.has(widget.eventType) === false,
	);
};

/** Full default widget preferences. OTT omissions are applied only at display time. */
export const getDefaultWidgetSettings = (): Record<InsightCategoryEnum, WidgetItem[]> =>
	DEFAULT_WIDGET_SETTINGS;

const RED = "#d3084c";
const GREEN = "#32ff7e";
const YELLOW = "#f6f355";

export const EVENT_COLOR_DEFINITION = {
	[EventOffensiveEnum.Shot]: [
		{
			key: PlayerStatType.ShotOnTarget,
			color: GREEN,
			label: "gamePage.playerStats.type.shotOnTarget",
		},
		{
			key: PlayerStatType.ShotOffTarget,
			color: RED,
			label: "gamePage.playerStats.type.shotOffTarget",
		},
		{
			key: PlayerStatType.ShotBlocked,
			color: YELLOW,
			label: "gamePage.playerStats.type.shotBlocked",
		},
	],
	[EventOffensiveEnum.TakeOn]: [
		{
			key: PlayerStatType.TakeOnSuccess,
			color: GREEN,
			label: "event.v3.flag.success.true",
		},
		{
			key: PlayerStatType.TakeOn,
			color: RED,
			label: "event.v3.flag.success.false",
		},
	],
	[EventDefensiveEnum.Tackle]: [
		{
			key: PlayerStatType.TackleSuccess,
			color: GREEN,
			label: "event.v3.flag.success.true",
		},
		{
			key: PlayerStatType.Tackle,
			color: RED,
			label: "event.v3.flag.success.false",
		},
	],
	[EventGoalkeeperEnum.CatchInterception]: [
		{
			key: PlayerStatType.CatchInterceptionSuccess,
			color: GREEN,
			label: "event.v3.flag.success.true",
		},
		{
			key: PlayerStatType.CatchInterception,
			color: RED,
			label: "event.v3.flag.success.false",
		},
	],
	[EventDefensiveEnum.TakeOnAgainst]: [
		{
			key: PlayerStatType.TakeOnAgainstSuccess,
			color: GREEN,
			label: "event.v3.flag.success.true",
		},
		{
			key: PlayerStatType.TakeOnAgainst,
			color: RED,
			label: "event.v3.flag.success.false",
		},
	],
	[EventDefensiveEnum.Card]: [
		{
			key: PlayerStatType.CardYellow,
			color: YELLOW,
			label: "event.v3.flag.card_type.yellow",
		},
		{
			key: PlayerStatType.CardRed,
			color: RED,
			label: "event.v3.flag.card_type.red",
		},
	],
	[EventDistributionEnum.AerialDuels]: [
		{
			key: PlayerStatType.AerialDuelSuccess,
			color: GREEN,
			label: "event.v3.flag.success.true",
		},
		{
			key: PlayerStatType.AerialDuel,
			color: RED,
			label: "event.v3.flag.success.false",
		},
	],
	[EventDistributionEnum.Pass]: [
		{
			key: PlayerStatType.PassSuccess,
			color: GREEN,
			label: "event.v3.flag.success.true",
		},
		{
			key: PlayerStatType.Pass,
			color: RED,
			label: "event.v3.flag.success.false",
		},
	],
	[EventDistributionEnum.PassReceived]: [
		{
			key: PlayerStatType.PassReceivedSuccess,
			color: GREEN,
			label: "event.v3.flag.success.true",
		},
		{
			key: PlayerStatType.PassReceived,
			color: RED,
			label: "event.v3.flag.success.false",
		},
	],
};

export type CustomTab = {
	label: string;
	key: string;
};

export enum TeamSideEnum {
	Home = "home",
	Away = "away",
}

export const DEFAULT_PERIODS = ["1", "2"];
export const CUSTOM_GROUP_KEY = "customGroup";
export const GAME_FILTERED_STATE_KEY = "gameFilteredState";
export const PASS_BODY_PART_FILTER_KEY = "event.distribution.pass.body_part";
export const SHOT_BODY_PART_FILTER_KEY = "event.offensive.shot.body_part";
export const SAVE_BODY_PART_FILTER_KEY = "event.goalkeeper.save.body_part";
